import { apiCommon } from 'src/api/ApiCommon';
import axios from 'axios';
import { buildValidator } from 'src_common/common/mobx-utils/buildValidator';
import * as t from 'io-ts';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FilesListCMSModelType } from 'src/api/config/filesCMS/getCMSFilesList';
import { Resource } from 'src_common/common/mobx-utils/Resource';

export const toBase64 = async (file: File | Blob): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (): void => resolve(reader.result);
        reader.onerror = (error): void => reject(error);
    });

export const ImgUploadIO = t.interface({
    img_urls: t.array(t.string),
});

export type ImgUploadResponseType = t.TypeOf<typeof ImgUploadIO>;

export const decodeImgUploadResponse = buildValidator('decodeImgUploadResponse', ImgUploadIO, true);

interface HandleUpdateResponseError {
    status: 'error';
    message?: string;
}

interface HandleUpdateResponseWarning {
    status: 'warning';
    message: string;
}

interface HandleUpdateResponseSuccess {
    status: 'success';
    data: ImgUploadResponseType;
}

export interface RequestImgBody {
    base64: string;
    img_type: string;
    mime_type: string;
}

export type HandleUploadImgResponse =
    | HandleUpdateResponseSuccess
    | HandleUpdateResponseError
    | HandleUpdateResponseWarning;

interface GetImagesLoading {
    type: 'loading';
}

interface GetImagesReady {
    type: 'ready';
    value: Array<FilesListCMSModelType>;
}

interface GetImagesError {
    type: 'error';
    value: string;
}

type GetImagesContentResponse = GetImagesLoading | GetImagesReady | GetImagesError;

export class FilesState {
    @observable private readonly imagesResource: Resource<Array<FilesListCMSModelType>>;
    @observable tempIsChooseFromLibrary: boolean = false;
    @observable showLibraryPopUp: boolean = false;

    constructor(directory: string) {
        this.imagesResource = new Resource(async (): Promise<Array<FilesListCMSModelType>> => {
            const response = await apiCommon.getCMSFilesList.run({ directory });
            return response;
        });
        makeAutoObservable(this);
    }

    uploadImage = async (baseData: RequestImgBody): Promise<ImgUploadResponseType | null> => {
        const response = await axios.request({
            method: 'POST',
            url: '/api-web/custom_request/post-upload-img',
            data: {
                baseData,
                directory: 'hero-widget',
            },
        });

        const decodedResponse = decodeImgUploadResponse(response.data);

        if (decodedResponse instanceof Error) {
            console.log('decodedBodyRequest', decodedResponse);
            console.error(decodedResponse);
            return null;
        }

        return decodedResponse;
    };

    public handleUploadImage = async (
        image: File,
        directory: string,
        wantedFormat?: string
    ): Promise<HandleUploadImgResponse> => {
        const img_size = Math.round(image.size / 1000);
        if (img_size < 300) {
            // < 300 kb
            return await this.handleUploadImageInner(image, directory, wantedFormat);
        }

        if (img_size < 2000) {
            // < 1000 kb
            return {
                status: 'warning',
                message: `File could be smaller (file size: ${img_size}kb).`,
            };
        }

        return {
            status: 'error',
            message: `File too big (file size: ${img_size}kb).`,
        };
    };

    public handleForceUploadImage = async (
        image: File,
        directory: string,
        wantedFormat?: string
    ): Promise<HandleUploadImgResponse> => {
        const img_size = Math.round(image.size / 1000);
        if (img_size > 2000) {
            // < 1000 kb
            return {
                status: 'error',
                message: `File too big (file size: ${img_size}kb).`,
            };
        }
        const image64 = await toBase64(image);
        if (image64 !== null) {
            const base64String = image64.toString();
            const formatStart = base64String.indexOf('/') + 1;
            const endData = base64String.indexOf(';');
            const imgType = base64String.slice(formatStart, endData);
            if (wantedFormat !== undefined && imgType !== wantedFormat) {
                return {
                    status: 'error',
                    message: 'Wrong format',
                };
            }
        }

        return await this.handleUploadImageInner(image, directory);
    };

    private handleUploadImageInner = async (
        image: File,
        directory: string,
        wantedFormat?: string
    ): Promise<HandleUploadImgResponse> => {
        const image64 = await toBase64(image);
        if (image64 !== null) {
            const base64String = image64.toString();

            const mimeTypeStart = base64String.indexOf(':') + 1;
            const formatStart = base64String.indexOf('/') + 1;
            const endData = base64String.indexOf(';');
            const base64Start = base64String.indexOf(',') + 1;
            const imgType = base64String.slice(formatStart, endData);
            const mimeType = base64String.slice(mimeTypeStart, endData);
            const base64 = base64String.slice(base64Start);

            const body = {
                base64,
                img_type: imgType,
                mime_type: mimeType,
                directory: directory,
            };

            if (wantedFormat !== undefined && imgType !== wantedFormat) {
                return {
                    status: 'error',
                    message: 'Wrong format',
                };
            }

            try {
                const response = await this.uploadImage(body);

                if (response === null) {
                    return {
                        status: 'error',
                    };
                }

                return {
                    status: 'success',
                    data: response,
                };
            } catch (error) {
                return {
                    status: 'error',
                };
            }
        }
        return {
            status: 'error',
        };
    };

    @computed get imagesListResource(): GetImagesContentResponse {
        const response = this.imagesResource.get();

        if (response.type === 'ready' && response.value !== null) {
            return {
                type: 'ready',
                value: response.value,
            };
        }
        if (response.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching hero widget info',
        };
    }

    @computed get imagesList(): Array<FilesListCMSModelType> {
        if (this.imagesListResource.type === 'ready') {
            return this.imagesListResource.value;
        }

        return [];
    }

    @computed get isLoading(): boolean {
        return this.imagesListResource.type === 'loading';
    }

    @computed get errorMessage(): string | null {
        if (this.imagesListResource.type === 'error') {
            return this.imagesListResource.value;
        }

        return null;
    }

    // @computed get hasSVGFormat(): boolean {
    //     if (this.imagesListResource.type === 'ready') {

    //     }
    // }

    @action handleHidePopup = (): void => {
        this.showLibraryPopUp = false;
    };

    @action onShowLibraryPopUp = async (): Promise<void> => {
        this.imagesResource.refresh();
        this.showLibraryPopUp = true;
    };

    @action handleDeleteImage = async (directory: string): Promise<void> => {
        await apiCommon.deleteCMSFile.run({ directory });
        this.imagesResource.refresh();
    };
}
